// contactToFormState.ts — Umkehrung der beiden Anlage-Payload-Builder:
// aus einem geladenen Kontakt (API-Form) den Formular-State der Anlage-Masken
// bauen, damit die Umwandlung Firma ↔ Person dieselben Formulare VORAUSGEFÜLLT
// zeigen kann.
//
// Gegenstücke:
//   buildPrivateContactCreatePayload.ts  (Person → API)
//   buildCompanyContactCreatePayload.ts  (Firma  → API)
// Die Feldnamen hier müssen zu denen dort passen — sie sind die Schlüssel des
// Formular-States, nicht die der Entity.
//
// Cross-Typ ist der Sinn der Sache: beim Umwandeln wird eine FIRMA in den
// Person-State übersetzt (und umgekehrt). Was im Ziel keine Entsprechung hat,
// bleibt leer und wird auf der Seite als „bitte ergänzen“ sichtbar.

// Seit 27.09.2026 zusätzlich die Grundlage des Bearbeiten-Modus der Akte
// („Allgemein" = Anlage-Formular, docs/analyse-akte-allgemein-gleich-anlage.md).
// Dafür bildet sie ALLE Felder ab, die die Builder lesen, und trägt die
// Datenbank-Ids der Listenzeilen mit (`addressDbId`, `bankDbId`, `commDbIds`,
// `dbId` je Zeile in den JSON-Listen). Nur so kann das Speichern
// (anlageUpdatePayload.ts) Bestandszeilen ändern statt sie neu anzulegen.

import { parseGeschaeftsjahr } from "./geschaeftsjahr";
import { herkunftZerlegen } from "./kontaktherkunft";
import { herkunftBestandFelder } from "../components/herkunft/herkunftBezug";
import { eigenerNewsletterFormState } from "./eigenerNewsletter";
import { jahreseinkommenAnzeige } from "./steuerUndAusweisAngaben";
import { zuwendungenAusApi } from "../data/zuwendungenForm";
import { todesfallBelegeAusApi } from "../data/todesfallBelege";
import { gruppeVonRolle } from "../data/rollenKatalog";
import { rollenAusStatus, rollenInsFeld } from "../data/kontaktstatusSegment";
import { foListeAusKontakt } from "./foUserIds";
import { eigenerAnschriftName } from "../data/adresstypen";

type AnyRec = Record<string, any>;

/**
 * privat/geschäftlich — wie `CommLabel` in useContactGeneralForm.
 *
 * Exportiert, weil der Telefonie-Zuordnen-Dialog dieselben Label-Werte schreibt
 * (`assignNumberUtils.NUMMER_ZUORDNUNGEN`): laufen die Schreibweisen
 * auseinander, findet die Kontaktmaske die Nummer nicht mehr. Über diesen Typ
 * fällt das beim type-check auf statt erst in der Maske.
 */
export type CommLabel = "privat" | "geschäftlich";

const s = (v: unknown): string =>
  v == null ? "" : typeof v === "string" ? v : String(v);

/** Datum aus der API (ISO oder Date) → `YYYY-MM-DD` fürs <input type="date">. */
const isoDate = (v: unknown): string => {
  if (!v) return "";
  const d = typeof v === "string" ? v : String(v);
  const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
};

/**
 * Kommunikationszeile nach Typ und Art — kind-first (SP5): die strukturierte
 * Spalte gewinnt, Bestand ohne `kind` wird weiter über das Label gefunden.
 * Firmen haben historisch weder kind noch Label (nur `isDefault` je Typ) —
 * deshalb fällt „privat" auf die Default-Zeile zurück, exakt wie `commValue`
 * in useContactGeneralForm. Genau das macht die Firmen-Rufnummer beim
 * Umwandeln zur „Telefon (privat)"-Vorbelegung.
 *
 * `belegt` sind Zeilen, die schon ein anderes Feld trägt. Jede Zeile landet in
 * genau EINEM Feld — sonst stünde dieselbe Nummer als privat und geschäftlich
 * da und das Speichern schriebe sie doppelt.
 */
const commRow = (
  comms: AnyRec[] | undefined,
  type: string,
  label: CommLabel,
  belegt: Set<AnyRec> = new Set(),
): AnyRec | undefined => {
  const list = (comms ?? []).filter((c) => c && c.type === type && !belegt.has(c) && s(c.value));
  const kind = label === "privat" ? "privat" : "geschaeftlich";
  const byKind = list.find((c) => c.kind === kind);
  if (byKind) return byKind;
  const labeled = list.find((c) => c.kind == null && (c.label ?? "").toLowerCase() === label);
  if (labeled) return labeled;
  if (label === "privat") {
    const def = list.find((c) => c.kind == null && c.isDefault);
    if (def) return def;
    // Auch ohne isDefault: die erste Zeile dieses Typs ist besser als nichts.
    return list.find((c) => c.kind == null);
  }
  return undefined;
};

/** Kanäle, die die Masken als eigene Felder führen (Social Media). */
const SOCIAL = ["linkedin", "instagram", "facebook", "tiktok", "xing", "youtube"] as const;

/** Typen, die als „weiterer Kommunikationsweg" gespeichert werden können. */
const WEITERE_TYPEN = new Set(["phone", "mobile", "email", "fax", "homepage", ...SOCIAL]);

/** Firmenname aus „Geschäftlich (Firma X)" — Rückfall, wenn `company` fehlt. */
const firmaAusLabel = (label: unknown): string => {
  const m = s(label).match(/\(([^)]+)\)\s*$/);
  return m ? m[1].trim() : "";
};

type Verteilung = {
  felder: Record<string, string>;
  /** Feldname → Datenbank-Id der Zeile, die das Feld trägt. */
  ids: Record<string, number>;
  /** Zeilen, die kein eigenes Feld haben, im Format `WeitereKommunikationEintrag`. */
  weitere: AnyRec[];
  /** Geschäftliche Zuordnung je Feld (`commCompanies` der Personenmaske), seit 02.10.2026 als Liste. */
  firmen: Record<string, Array<{ id: number; name: string }>>;
};

/**
 * Die Unternehmen einer gespeicherten Zeile. Die API liefert sie als
 * `companies: [{ id, name }]` (berechnete Spalte, contactKommunikationFirmen.ts);
 * ältere Antworten bzw. Testdaten kennen nur `companyId` — dann der Name aus
 * `company` oder dem Label „Geschäftlich (Firma X)".
 */
const firmenDerZeile = (row: AnyRec): Array<{ id: number; name: string }> => {
  if (Array.isArray(row.companies)) {
    return (row.companies as AnyRec[])
      .map((f) => ({ id: Number(f?.id), name: s(f?.name) }))
      .filter((f) => Number.isInteger(f.id) && f.id > 0 && f.name);
  }
  const firmaId = Number(row.companyId ?? row.company?.id);
  if (!Number.isInteger(firmaId) || firmaId <= 0) return [];
  const name = s(row.company?.displayName ?? row.company?.lastname) || firmaAusLabel(row.label);
  return [{ id: firmaId, name }];
};

/**
 * Alle Kommunikationszeilen auf die Felder einer Maske verteilen. Was kein
 * eigenes Feld hat, wird „weiterer Kommunikationsweg" — bis 27.09.2026 fiel es
 * hier still weg (Analyse P13/F11).
 */
const kommunikationVerteilen = (
  comms: AnyRec[] | undefined,
  slots: Array<{ feld: string; type: string; label?: CommLabel }>,
): Verteilung => {
  const belegt = new Set<AnyRec>();
  const out: Verteilung = { felder: {}, ids: {}, weitere: [], firmen: {} };
  for (const slot of slots) {
    const row = slot.label
      ? commRow(comms, slot.type, slot.label, belegt)
      : (comms ?? []).find((c) => c && c.type === slot.type && !belegt.has(c) && s(c.value));
    out.felder[slot.feld] = row ? s(row.value) : "";
    if (!row) continue;
    belegt.add(row);
    if (typeof row.id === "number") out.ids[slot.feld] = row.id;
    const firmen = firmenDerZeile(row);
    if (slot.label === "geschäftlich" && firmen.length) out.firmen[slot.feld] = firmen;
  }
  for (const row of comms ?? []) {
    if (!row || belegt.has(row) || !s(row.value) || !WEITERE_TYPEN.has(row.type)) continue;
    const firmen = firmenDerZeile(row);
    out.weitere.push({
      id: `wk-${row.id ?? out.weitere.length}`,
      ...(typeof row.id === "number" ? { dbId: row.id } : {}),
      // Die Maske führt die Website als „website", gespeichert wird „homepage".
      type: row.type === "homepage" ? "website" : row.type,
      label: s(row.label),
      value: s(row.value),
      ...(row.kind ? { zuordnung: row.kind } : {}),
      ...(firmen.length ? { firmen } : {}),
    });
  }
  return out;
};

/**
 * Gespeicherte Erreichbarkeit → allgemeiner Text + Angaben je Feld.
 *
 * Die Builder setzen sie als „allgemein | Label: Wert; Label: Wert" zusammen.
 * Hier die Umkehrung: bekannte Label werden wieder ihrem Feld zugeordnet,
 * unbekannte bleiben unter ihrem Label stehen — der Builder schreibt sie dann
 * genau so zurück. Ohne „ | " gilt der ganze Text als allgemein; auch das
 * ergibt beim Speichern denselben Text.
 */
const erreichbarkeitZerlegen = (
  wert: unknown,
  labels: Record<string, string>,
): { erreichbarkeit: string; erreichbarkeitenDetail: string } => {
  const text = s(wert);
  const trenner = text.indexOf(" | ");
  if (trenner < 0) return { erreichbarkeit: text, erreichbarkeitenDetail: "" };
  const zuFeld = new Map(Object.entries(labels).map(([feld, label]) => [label, feld]));
  const detail: Record<string, string> = {};
  for (const teil of text.slice(trenner + 3).split("; ")) {
    const doppelpunkt = teil.indexOf(": ");
    if (doppelpunkt < 0) continue;
    const label = teil.slice(0, doppelpunkt).trim();
    const v = teil.slice(doppelpunkt + 2).trim();
    if (label && v) detail[zuFeld.get(label) ?? label] = v;
  }
  return {
    erreichbarkeit: text.slice(0, trenner),
    erreichbarkeitenDetail: Object.keys(detail).length ? JSON.stringify(detail) : "",
  };
};

/** Ja/Nein-Radios der Firmenmaske („Ja" | "Nein" | ""). */
const jaNein = (v: unknown): string => (v === true ? "Ja" : v === false ? "Nein" : "");

/** Zahl fürs Eingabefeld — `null` bleibt leer, 0 bleibt 0. */
const zahl = (v: unknown): string => (v == null || v === "" ? "" : String(v));

/** Herkunft, Empfehlung, Einladung, Newsletter, Zuwendungen — bei Person und Firma gleich. */
const herkunftUndAnsprache = (c: AnyRec): AnyRec => {
  const kennzeichen = s(c.einladungKennzeichen);
  return {
    ...herkunftBestandFelder(c),
    contactOriginSelections: JSON.stringify(herkunftZerlegen(c.kontaktherkunft)),
    contactOriginOther: s(c.kontaktherkunftSonstiges),
    kontaktherkunftDetails: s(c.kontaktherkunftDetails),
    netzwerkInfo: s(c.netzwerkInfo),
    empfehlungsgeber: s(c.empfehlungsgeber),
    einladungKennzeichen: kennzeichen,
    // Die Priorität steckt im Kennzeichen selbst (A/B/C oder „immer").
    einladungPrioritaet: ["A", "B", "C"].includes(kennzeichen) ? kennzeichen : "",
    newsletter: c.newsletter === true ? "ja" : "",
    newsletterEmails: s(c.newsletterEmails),
    ...eigenerNewsletterFormState(c),
    // Die Server-Id jeder Zeile steht in `dbId` — der Abgleich
    // `zuwendungenErsetzen` erkennt Bestandszeilen daran.
    zuwendungen: JSON.stringify(zuwendungenAusApi(c.zuwendungen)),
    remark: s(c.remark),
  };
};

/** Erste Adresse (bevorzugt die Standard-Adresse). */
const mainAddress = (addresses: AnyRec[] | undefined): AnyRec => {
  const list = addresses ?? [];
  return list.find((a) => a.isDefault) ?? list[0] ?? {};
};

/**
 * Weitere Adressen (alle außer der Haupt-Adresse), im Repeater-Format.
 *
 * Als JSON-TEXT, nicht als Array: Die Masken lesen das Feld mit `parseArr`,
 * das nur Text versteht. Ein Array fiel dort still weg — beim Umwandeln gingen
 * so alle weiteren Anschriften verloren.
 */
const extraAddresses = (addresses: AnyRec[] | undefined, nurEigenerName = false): string => {
  const list = addresses ?? [];
  const main = mainAddress(list);
  return JSON.stringify(
    list
      .filter((a) => a !== main)
      .map((a, i) => ({
        id: `adr-${a.id ?? i}`,
        ...(typeof a.id === "number" ? { dbId: a.id } : {}),
        street: s(a.street),
        postalCode: s(a.postalCode),
        postalcode: s(a.postalCode),
        city: s(a.city),
        cityArea: s(a.ortsteil),
        country: s(a.country),
        // Person: Die Maske schrieb bei leerem Namen die Art-Beschriftung in
        // `addressDescription` („Ferienimmobilie"). Das ist kein Name — es
        // stünde sonst im Feld „Bezeichnung" und bliebe nach einem Artwechsel
        // hängen (T-358). Bei Firmen ist die Bezeichnung frei und bleibt.
        addressDescription: nurEigenerName ? eigenerAnschriftName(s(a.addressDescription)) : s(a.addressDescription),
        ...(a.addressType ? { addressType: s(a.addressType) } : {}),
      })),
  );
};

const mainBank = (banks: AnyRec[] | undefined): AnyRec => {
  const list = banks ?? [];
  return list.find((b) => b.isDefault) ?? list[0] ?? {};
};

const extraBanks = (banks: AnyRec[] | undefined): string => {
  const list = banks ?? [];
  const main = mainBank(list);
  return JSON.stringify(
    list
      .filter((b) => b !== main)
      .map((b, i) => ({
        id: `bank-${b.id ?? i}`,
        ...(typeof b.id === "number" ? { dbId: b.id } : {}),
        bankname: s(b.bankName),
        iban: s(b.iban),
        bic: s(b.bic),
        accountHolder: s(b.accountHolder),
        bankbezeichnung: s(b.bankDescription),
        directDebit: b.directDebit === true,
      })),
  );
};

/** Datenbank-Id der Hauptzeile — "" wenn es keine gibt. */
const dbIdOf = (row: AnyRec): string => (typeof row?.id === "number" ? String(row.id) : "");

/**
 * Die Zusatz-Status eines Kontakts.
 *
 * `GET /contact/:id` liefert sie als `extraStatuses` (sie hängen in
 * `ContactStatusLink`, nicht am Kontakt selbst). Fehlt das Feld — etwa weil der
 * Kontakt aus einer Liste stammt —, bleibt die Liste leer: dann gibt es nichts
 * zu spiegeln, und es wird auch nichts überschrieben.
 */
const extraStatuses = (c: AnyRec): string[] =>
  Array.isArray(c.extraStatuses) ? (c.extraStatuses as unknown[]).map(s).filter(Boolean) : [];

/**
 * Kontaktstatus auf das Formular-Vokabular abbilden.
 *
 * Die Anlage-Masken kennen nur „Interessent | Kunde | Sonstige“ plus ein
 * Freitextfeld für alles Weitere. Ein Bestandsstatus wie „Altkunde“,
 * „Haushaltsmitglied“ oder „Verantwortung fürs Unternehmen“ würde sonst beim Speichern
 * still zu „Interessent“ — der Kontakt verlöre seinen Status durch die reine
 * Typ-Umwandlung.
 */
export const mapStatusToForm = (
  status: string | undefined,
  /**
   * Die Zusatz-Status des Kontakts (`ContactStatusLink`). Sie liegen NICHT am
   * Kontakt selbst — ohne sie verlöre jede Umwandlung still alle Nebenrollen.
   */
  zusatz: string[] = [],
): {
  kontaktStatus: string;
  kontaktStatusSonstig: string;
  kontaktStatusRolle: string;
  kontaktStatusRollen: string;
  kontaktStatusAuch: string;
} => {
  const v = (status ?? "").trim();
  const mehrfach = rollenAusStatus(v, zusatz);
  // Die beiden Alt-Felder bleiben gefüllt: `SinglePageForm` und
  // `SinglePageCompanyForm` (Akte) zeigen den Status weiterhin in zwei
  // Dropdowns. Maßgeblich für das Speichern ist die Liste — siehe
  // `buildPrivateContactCreatePayload`.
  const ersteRolle = mehrfach.rollen[0] ?? "";
  const alt = altFelder(v, ersteRolle);

  return {
    kontaktStatus: mehrfach.kontaktStatus,
    kontaktStatusRollen: rollenInsFeld(mehrfach.rollen),
    kontaktStatusAuch: mehrfach.auchKundeOderInteressent,
    ...alt,
  };
};

/** Untertyp + Rolle für die beiden Akte-Masken, die weiter zwei Dropdowns zeigen. */
const altFelder = (
  primaer: string,
  ersteRolle: string,
): { kontaktStatusSonstig: string; kontaktStatusRolle: string } => {
  if (primaer === "Kunde" || primaer === "Interessent" || !primaer) {
    // Ein Kunde MIT Nebenrollen: die erste davon gehört in die Alt-Felder,
    // sonst stünde in der Akte „Sonstige" ohne jede Angabe.
    if (!ersteRolle) return { kontaktStatusSonstig: "", kontaktStatusRolle: "" };
  }
  const wert = ersteRolle || primaer;
  // Eine Rolle (z. B. „Sachverständiger“) steht im Dropdown nicht selbst,
  // sondern hinter ihrer Gruppe. Ohne diese Rückabbildung stünde im ersten
  // Feld ein Wert, den seine Optionsliste nicht kennt — es sähe leer aus,
  // obwohl der Kontakt einen Status hat.
  const gruppe = gruppeVonRolle(wert);
  if (gruppe) return { kontaktStatusSonstig: gruppe, kontaktStatusRolle: wert };
  // Alles andere (Sonstige, Altkunde, Haushaltsmitglied, …) → der Originalwert
  // im Freitextfeld, damit er erhalten bleibt.
  return {
    kontaktStatusSonstig: wert === "Sonstige" ? "" : wert,
    kontaktStatusRolle: "",
  };
};

/** Verantwortlichkeit (FO/BO) aus dem Kontakt in den Formular-State. */
const ownershipOf = (c: AnyRec): AnyRec => ({
  foUserId: s(c.foUserId ?? c.ownerUserId),
  // Mehrere FO (28.09.2026): kommagetrennt im Formular-State, erster = foUserId.
  foUserIds: foListeAusKontakt(c).join(","),
  boType: s(c.boType) || "NONE",
  boUserId: s(c.boUserId),
  boTeamId: s(c.boTeamId ?? c.ownerTeamId),
  inhouseContactUserId: s(c.inhouseContactUserId),
});

/** Anschrift-Felder (beide Schreibweisen, die die Masken lesen) + Zeilen-Id. */
const anschrift = (c: AnyRec, person = false): AnyRec => {
  const addr = mainAddress(c.addresses);
  // NUR die Feldnamen, die die Maske auch bearbeitet. Bis 28.09.2026 standen
  // hier zusätzlich die Schreibweisen `strasse`/`plz`/`postalCode`/`ort`/
  // `ortsteil`/`land`, die die Builder als Rückfall lesen (`firstNonEmpty`).
  // Leerte der Anwender in der Akte ein Feld, blieb der Wert im Rückfall-
  // Schlüssel stehen — der Builder nahm ihn, und nach dem Speichern stand
  // der alte Wert wieder da (PROD-Befund 28.09.2026, Standard-Telefon).
  return {
    street: s(addr.street),
    // Das Eingabefeld heißt `postalcode` (klein) — bis 27.09.2026 blieb es
    // beim Umwandeln leer (Analyse P1/F1).
    postalcode: s(addr.postalCode),
    city: s(addr.city),
    cityArea: s(addr.ortsteil),
    country: s(addr.country),
    addressDbId: dbIdOf(addr),
    additionalAddresses: extraAddresses(c.addresses, person),
  };
};

/** Bank-Felder + Zeilen-Id. */
const bankverbindung = (c: AnyRec): AnyRec => {
  const bank = mainBank(c.bankAccounts);
  return {
    bankname: s(bank.bankName),
    iban: s(bank.iban),
    bic: s(bank.bic),
    accountHolder: s(bank.accountHolder),
    bankbezeichnung: s(bank.bankDescription),
    // Lastschrift hat (noch) kein Feld in der Anlage (B9). Mitgetragen, damit
    // das Speichern der Liste sie nicht auf „nein" zurücksetzt.
    bankDirectDebit: bank.directDebit === true ? "ja" : "",
    bankDbId: dbIdOf(bank),
    additionalBanks: extraBanks(c.bankAccounts),
  };
};

/** Verfügungen im Format der Personenmaske (`verfuegungen`, JSON). */
const verfuegungenFuerFeld = (rows: unknown): string =>
  JSON.stringify(
    (Array.isArray(rows) ? rows : [])
      .filter((v: AnyRec) => s(v?.art))
      .map((v: AnyRec) => {
        const b = v.bevollmaechtigterContact;
        const name =
          s(v.bevollmaechtigterName) ||
          (b ? s(b.displayName) || `${s(b.lastname)}${b.firstName ? ", " + s(b.firstName) : ""}` : "");
        return {
          art: s(v.art),
          bevollmaechtigterContactId: v.bevollmaechtigterContactId ?? b?.id ?? null,
          bevollmaechtigterName: name || null,
          bevollmaechtigterRef: b?.id
            ? { contactId: b.id, type: "Person", displayName: name || undefined }
            : null,
        };
      }),
  );

/**
 * Kontakt → State der PRIVAT-Anlagemaske (`NewPrivateContactForm`).
 *
 * Zwei Aufrufer: die Umwandlung Firma → Person (der Firmenname landet als
 * Nachname, die Firmen-Kommunikation als „privat", Firmenfelder entfallen) und
 * der Bearbeiten-Modus der Akte, der eine Person in ihre eigene Maske lädt.
 */
export function contactToPrivateFormState(c: AnyRec): AnyRec {
  const status = mapStatusToForm(c.status?.status ?? c.statusName, extraStatuses(c));
  // Geschäftlich zuerst: Die Privat-Felder fallen notfalls auf eine Zeile ohne
  // Art zurück — sie dürfen keine nehmen, die ausdrücklich geschäftlich ist.
  const k = kommunikationVerteilen(c.communications, [
    { feld: "phoneWork", type: "phone", label: "geschäftlich" },
    { feld: "mobileWork", type: "mobile", label: "geschäftlich" },
    { feld: "emailWork", type: "email", label: "geschäftlich" },
    { feld: "phonePrivate", type: "phone", label: "privat" },
    { feld: "mobilePrivate", type: "mobile", label: "privat" },
    { feld: "emailPrivate", type: "email", label: "privat" },
    ...SOCIAL.map((t) => ({ feld: t, type: t })),
  ]);
  const erreichbar = erreichbarkeitZerlegen(c.erreichbarkeit, {
    phonePrivate: "Telefon (privat)",
    mobilePrivate: "Mobil (privat)",
    phoneWork: "Telefon (geschäftlich)",
    mobileWork: "Mobil (geschäftlich)",
  });

  return {
    // Name: bei einer Firma steht der Firmenname in `lastname` — er bleibt der
    // Nachname, den man auf der Seite korrigieren kann.
    lastname: s(c.lastname),
    firstname: s(c.firstName),
    nickname: s(c.callName),
    // Das Feld „anrede“ trägt seit 08/2026 die echte Anrede, nicht mehr das
    // Geschlecht. Beim Umwandeln Firma → Person kommt sie aus dem Katalogwert;
    // fehlt der (Altbestand ohne Anrede), bleibt das Feld leer und die
    // Sachbearbeitung wählt sie.
    anrede: s(c.salutation?.salutation ?? c.salutationName),
    title: s(c.title?.title ?? c.titleName),

    ...status,

    placeofbirth: s(c.birthPlace),
    countryofbirth: s(c.birthCountry),
    // Firma → Person: das Gründungsdatum ist beim Convert bereits ins
    // Geburtsdatum übernommen worden; hier trotzdem beide Quellen lesen.
    dateofbirth: isoDate(c.birthDate ?? c.gruendungsdatum),
    birthname: s(c.birthName),
    citizenship: s(c.nationality),
    birthTime: s(c.birthTime),
    birthWeightGrams: zahl(c.birthWeightGrams),
    birthHeightCm: zahl(c.birthHeightCm),

    familyStatus: s(c.maritalStatus),
    familyStatusOther: "",
    weddingDateCivil: isoDate(c.weddingDateCivil),
    weddingDateChurch: isoDate(c.weddingDateChurch),

    // Verstorben: Das Formularfeld kennt nur „ja" oder leer.
    deceased: c.deceased === true ? "ja" : "",
    deceasedAt: isoDate(c.deceasedAt),
    // Belege zum Todesfall (T-357) als JSON-Liste mit Server-Id je Zeile —
    // der Abgleich im Backend erkennt Bestandszeilen daran. Die Altspalten
    // `sterbeanzeigeUrl`/`-Name` hat die Migration 202610220201 übernommen.
    todesfallBelege: JSON.stringify(todesfallBelegeAusApi(c.todesfallBelege)),

    // Dokumente / Ausweisdaten
    idCardNumber: s(c.idCardNumber),
    idCardType: s(c.idCardType),
    idCardSubtype: s(c.idCardSubtype),
    idCardClasses: s(c.idCardClasses),
    idCardValidUntil: isoDate(c.idCardValidUntil),
    idCardIssuedAt: isoDate(c.idCardIssuedAt),
    idCardAuthority: s(c.idCardAuthority),
    additionalDocuments: s(c.additionalDocuments),

    // Steuer & Sozialdaten — geschützte Felder. Fehlen sie in der Antwort
    // (kein Recht), bleiben sie leer; der Diff schickt sie dann nicht mit.
    taxIdNumber: s(c.taxIdNumber),
    taxNumber: s(c.taxNumber),
    taxClass: s(c.taxClass),
    socialSecurityNumber: s(c.socialSecurityNumber),
    // „84.000" statt „84000.00": Der Builder liest die Eingabe streng de-DE —
    // der rohe Spaltenwert käme als hundertfacher Betrag zurück.
    annualIncome: jahreseinkommenAnzeige(c.annualIncome),

    occupationStatus: s(c.jobStatus),
    employer: s(c.jobEmployer ?? c.employer),
    industry: s(c.jobIndustry ?? c.industry),
    position: s(c.jobOccupation ?? c.occupation),
    zusaetzlichSelbststaendig: s(c.zusaetzlichSelbststaendig),
    // Rentner mit weiterer Tätigkeit (data/berufRentner.ts).
    weitereTaetigkeit: s(c.weitereTaetigkeit),

    hobbys: s(c.hobbys),
    vorlieben: s(c.vorlieben),
    memberships: s(c.memberships),

    ...anschrift(c, true),

    // Kommunikation — „privat“ fällt auf die Firmen-Default-Zeile zurück.
    ...k.felder,
    commDbIds: JSON.stringify(k.ids),
    commCompanies: JSON.stringify(k.firmen),
    weitereKommunikation: JSON.stringify(k.weitere),
    ...erreichbar,

    ...bankverbindung(c),

    ...herkunftUndAnsprache(c),
    verfuegungen: verfuegungenFuerFeld(c.verfuegungen),

    ...ownershipOf(c),
  };
}

/** Eine Zeile aus `GET contact/:id/branchengruppen` (erste = Hauptbranche). */
export type BranchengruppeZeile = { branchengruppeId: number; freitext?: string | null };

/**
 * Kontakt → State der FIRMEN-Anlagemaske (`NewCompanyContactForm`).
 *
 * Zwei Aufrufer: die Umwandlung Person → Firma (der Name der Person wird zum
 * Firmennamen, private und geschäftliche Rufnummern bleiben als zwei
 * Kommunikationswege erhalten) und der Bearbeiten-Modus der Akte.
 *
 * `extras.branchengruppen`: Die weiteren Branchen haben einen eigenen
 * Endpunkt. Wer sie mitgibt, bekommt sie in `weitereBranchengruppen`.
 */
export function contactToCompanyFormState(
  c: AnyRec,
  extras: { branchengruppen?: BranchengruppeZeile[] } = {},
): AnyRec {
  const status = mapStatusToForm(c.status?.status ?? c.statusName, extraStatuses(c));

  // Person → Firma: „Nachname, Vorname“ ergibt den Firmennamen-Vorschlag.
  const name = s(c.firstName)
    ? `${s(c.lastname)}, ${s(c.firstName)}`
    : s(c.lastname);

  // Die Firmenmaske führt je Art EIN Feld. Vorbelegt wird der private (bzw.
  // der Default) Wert; alles Weitere wird „weiterer Kommunikationsweg".
  const k = kommunikationVerteilen(c.communications, [
    { feld: "phone", type: "phone", label: "privat" },
    { feld: "mobile", type: "mobile", label: "privat" },
    { feld: "email", type: "email", label: "privat" },
    { feld: "fax", type: "fax" },
    { feld: "website", type: "homepage" },
    ...SOCIAL.map((t) => ({ feld: t, type: t })),
  ]);
  const erreichbar = erreichbarkeitZerlegen(c.erreichbarkeit, {
    phone: "Telefon (Zentrale)",
    mobile: "Mobil",
  });
  // Alle Branchen gleichrangig (User 28.09.2026): maßgeblich ist die Liste
  // aus ContactBranchengruppeZuordnung. Steht in der Kontaktspalte eine
  // Gruppe, die dort fehlt (Altbestand), kommt sie vorne dazu — sonst ginge
  // beim nächsten Speichern entweder sie oder die erste Listenzeile verloren.
  const branchen: BranchengruppeZeile[] = [...(extras.branchengruppen ?? [])];
  if (c.branchengruppeId != null && !branchen.some((b) => b.branchengruppeId === c.branchengruppeId)) {
    branchen.unshift({ branchengruppeId: c.branchengruppeId, freitext: c.brancheFreitext ?? null });
  }
  const hauptBranche = branchen[0]?.branchengruppeId;

  return {
    name,
    lastname: s(c.lastname),
    displayName: s(c.displayName) || name,
    // Der Namenszusatz gilt nur für Firmen, überlebt aber eine Umwandlung zur
    // Person (beim Convert wird er nicht geleert). Auf dem Rückweg zur Firma
    // steht er dadurch wieder im Feld, statt still verloren zu gehen.
    suffix: s(c.suffix),
    kurzname: s(c.kurzname),
    partnerStatus: s(c.partnerStatus),
    partnerAbrechnungDirekt: c.partnerAbrechnungDirekt === true ? "true" : c.partnerAbrechnungDirekt === false ? "false" : "",
    gesellschaftId: c.gesellschaftId != null ? String(c.gesellschaftId) : "",
    unternehmensfunktion: s(c.unternehmensfunktion),

    ...status,

    // Kein `legalForm` daneben — das Eingabefeld ist `gesellschaftsform`, der
    // Builder liest `legalForm` nur als Rückfall (sonst ließe sich die
    // Rechtsform in der Akte nicht leeren, s. `anschrift`).
    legalFormId: c.legalForm?.id != null ? String(c.legalForm.id) : "",
    // Rechtsvorgänger (frühere Rechtsform) — Katalog-Id; der Name nur für den
    // Fall, dass der Eintrag inzwischen deaktiviert ist.
    oldLegalFormId: c.oldLegalForm?.id != null ? String(c.oldLegalForm.id) : "",
    oldLegalFormName: s(c.oldLegalForm?.legalForm),
    gesellschaftsform: s(c.legalForm?.legalForm ?? c.rechtsform),
    // Konkrete Bezeichnung bei „Sonstige"/„Ausländische Rechtsform".
    rechtsform: s(c.rechtsform),
    handelsregisterNummer: s(c.handelsregisterNummer),
    register: s(c.register),
    registergericht: s(c.registergericht),
    umsatzsteuernummer: s(c.umsatzsteuernummer),
    // Die Firmenanlage schreibt `steuernummer`; `taxNumber` ist die Spalte der
    // Person und nur der Rückfall für eine umgewandelte Person.
    steuernummer: s(c.steuernummer ?? c.taxNumber),
    finanzamt: s(c.finanzamt),
    finanzamtBufa: s(c.finanzamtBufa),
    vorsteuerabzugsberechtigt: jaNein(c.vorsteuerabzugsberechtigt),
    istGemeinnuetzig: jaNein(c.istGemeinnuetzig),
    freistellungsbescheidBis: isoDate(c.freistellungsbescheidBis),
    zweckNrAO: s(c.zweckNrAO),
    // Person → Firma: das Geburtsdatum wird beim Convert zum Gründungsdatum.
    gruendungsdatum: isoDate(c.gruendungsdatum ?? c.birthDate),
    // Geschäftsjahr (Bilanzstichtag) → die zwei Auswahlfelder der Anlage. Fehlte
    // hier: eine Firma→Person→Firma-Umwandlung kam mit leerem Geschäftsjahr
    // zurück und überschrieb beim Speichern den gespeicherten Stichtag.
    geschaeftsjahrTag: parseGeschaeftsjahr(c.financialYearEnd).tag,
    geschaeftsjahrMonat: parseGeschaeftsjahr(c.financialYearEnd).monat,
    memberships: s(c.memberships),
    brancheClassId: c.brancheClassId != null ? String(c.brancheClassId) : "",
    branchengruppeId: hauptBranche != null ? String(hauptBranche) : "",
    brancheFreitext: s(
      branchen[0]?.freitext ?? (hauptBranche === c.branchengruppeId ? c.brancheFreitext : null),
    ),
    weitereBranchengruppen: JSON.stringify(
      branchen.slice(1).map((b) => ({
        branchengruppeId: b.branchengruppeId,
        freitext: s(b.freitext),
      })),
    ),
    industry: s(c.jobIndustry ?? c.industry),
    taetigkeitsbeschreibung: s(c.jobOccupation),
    satzungszweck: s(c.satzungszweck),

    ...anschrift(c),

    // Kein `telefon`/`mobil` daneben: Der Builder liest sie als Rückfall —
    // ein in der Akte geleertes Standard-Telefon kam sonst beim Speichern
    // aus dem Rückfall zurück (PROD-Befund 28.09.2026).
    ...k.felder,
    commDbIds: JSON.stringify(k.ids),
    weitereKommunikation: JSON.stringify(k.weitere),
    ...erreichbar,

    ...bankverbindung(c),

    ...herkunftUndAnsprache(c),

    ...ownershipOf(c),
  };
}
